import {
  getReportsFindSimilarReportsQueryOptions,
  type ReportResponseDto,
  type ReportsFindSimilarReportsParams,
} from '@/api/WhisApi/generated';
import { mapReportToSummary } from '@/components/ReportList/utils/mapReportToSummary';
import { showToast } from '@/components/Toast/toast.service';
import { useAppTranslation } from '@/hooks/useAppTranslation';
import { useOfflineDegradedMode } from '@/hooks/useOfflineDegradedMode';
import { isOfflineSubmissionError } from '@/screens/ReportCreation/hooks/offlineReportSubmissionQueue/useOfflineReportSubmissionQueue';
import type { ReportFormData } from '@/screens/ReportCreation/reportCreation.types';
import { buildSimilarReportsParams } from '@/screens/ReportCreation/reportCreation.utils';
import i18n from '@/store/i18n';
import { useSimilarReportsStore } from '@/store/similarReports/similarReports.store';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter, useSegments } from 'expo-router';
import { useState } from 'react';

interface UseSimilarReportsCheckProps {
  reportCode?: string;
  reportId?: string;
  report?: ReportResponseDto | null;
  onPublish: (values: ReportFormData) => Promise<void>;
}

interface UseSimilarReportsCheckResult {
  isCheckingSimilarReports: boolean;
  triggerSimilarReportsCheck: (values: ReportFormData) => boolean;
}

export const useSimilarReportsCheck = ({
  reportCode,
  reportId,
  report,
  onPublish,
}: UseSimilarReportsCheckProps): UseSimilarReportsCheckResult => {
  const { t } = useAppTranslation();
  const router = useRouter();
  const segments = useSegments() as string[];
  const queryClient = useQueryClient();
  const setSimilarReportsState = useSimilarReportsStore(
    (store) => store.setState,
  );
  const { isOfflineDegradedMode } = useOfflineDegradedMode();
  const [isCheckingSimilarReports, setIsCheckingSimilarReports] =
    useState(false);

  const isBoardRoute = segments.includes('board');
  const isEditRoute = segments.includes('edit');

  const checkSimilarReports = async (
    params: ReportsFindSimilarReportsParams,
    values: ReportFormData,
  ): Promise<void> => {
    setIsCheckingSimilarReports(true);
    try {
      const queryOptions = getReportsFindSimilarReportsQueryOptions(params);
      const response = await queryClient.fetchQuery({
        ...queryOptions,
        networkMode: 'always',
        retry: false,
      });
      const reports = response?.reports ?? [];
      const filteredReports = reportId
        ? reports.filter((report) => report.id !== reportId)
        : reports;

      if (filteredReports.length > 0) {
        setSimilarReportsState({
          params,
          reports: filteredReports.map((report) =>
            mapReportToSummary(report, i18n),
          ),
          pendingValues: values,
          reportCode,
          report,
        });
        if (isEditRoute && reportId) {
          router.push({
            pathname: isBoardRoute
              ? '/(tabs)/board/reports/[reportId]/edit/similar'
              : '/(tabs)/reports/[reportId]/edit/similar',
            params: { reportId },
          });
          return;
        }

        router.push('/(tabs)/report-creation/similar');
        return;
      }

      await onPublish(values);
    } catch (error) {
      if (isOfflineSubmissionError(error)) {
        await onPublish(values);
        return;
      }

      showToast({
        type: 'error',
        title: t('reportCreation.toasts.similarReportsError'),
      });
    } finally {
      setIsCheckingSimilarReports(false);
    }
  };

  const triggerSimilarReportsCheck = (values: ReportFormData): boolean => {
    if (isOfflineDegradedMode) {
      void onPublish(values);
      return true;
    }

    const params = buildSimilarReportsParams(values);

    if (!params) {
      showToast({
        type: 'error',
        title: t('reportCreation.toasts.similarReportsMissingLocation'),
      });
      return false;
    }

    void checkSimilarReports(params, values);
    return true;
  };

  return {
    isCheckingSimilarReports,
    triggerSimilarReportsCheck,
  };
};
